/**
 * The breakdown modal's three exports — Copy Image, Print PDF, Copy Text — in
 * the office's own format when it has one, else the standard ones.
 *
 * The office is the BREAKDOWN's (`inputs.breakdown.office`), so FTC staff
 * working another office export in that office's format. A super admin may
 * choose a draft, or the standard exports, to compare (`choice`).
 *
 * Nothing here can cost the user their export: if the office's format can't be
 * loaded or drawn, the standard export is made instead and `result.warning`
 * says so. The clipboard writes are started synchronously, before anything is
 * awaited, as in the standard path (see ../clipboard.js).
 */
import { renderBreakdownImage } from "../breakdownImage";
import { copyBlobToClipboard, copyTextToClipboard } from "../clipboard";
import { buildBreakdownExportModel } from "../exportModel";
import { renderPlanInfoText } from "../planInfoText";
import { buildBreakdownPrintHtml, printHtmlDocument } from "../printDocument";
import { renderShortFormText } from "../shortFormText";
import { resolveTemplate } from "./formatFacts";
import { printFormatPages } from "./formatPrint";
import { renderFormatText } from "./formatText";
import { isUsableId, loadFormatImages, loadOfficeFormat } from "./officeFormat";
import { paintFormatImage, paintPages } from "./paintFormat";

export const NOTHING_TO_EXPORT = "nothing to export";
export const FALLBACK_WARNING = "This office's own breakdown format couldn't be used, so this is the standard breakdown.";

/** The office whose format a breakdown is exported in: the breakdown's own. */
export function exportOfficeId(inputs, fallbackOfficeId) {
  const own = inputs && inputs.breakdown && inputs.breakdown.office;
  return isUsableId(own) ? own : fallbackOfficeId;
}

/**
 * {format, images, values, model} when the click exports the office's own
 * format, null for the standard exports. `choice`: "auto" (the office's live
 * format), "standard", or a draft's id. Throws when a format should be used but
 * couldn't be loaded.
 */
export async function chooseFormat(inputs, { officeId, choice = "auto", withImages = true } = {}) {
  if (choice === "standard") return null;
  const office = exportOfficeId(inputs, officeId);
  if (!isUsableId(office)) return null;
  const loaded = await loadOfficeFormat(office, { formatId: isUsableId(choice) ? choice : null });
  if (!loaded) throw new Error("the office's format couldn't be loaded");
  if (!loaded.format) return null;
  const images = withImages ? await loadFormatImages(loaded.format) : null;
  const { values, model } = resolveTemplate(loaded.format.template, inputs);
  return { format: loaded.format, images, values, model };
}

async function choose(inputs, opts, result) {
  try {
    return await chooseFormat(inputs, opts);
  } catch (err) {
    console.error("Office breakdown format unavailable:", err);
    // Only worth saying when the buttons promised the office's format.
    if (opts.expected || isUsableId(opts.choice)) result.warning = FALLBACK_WARNING;
    return null;
  }
}

/**
 * Copy Image. Returns {blobPromise, copied, result}; result: {filename, custom, warning}.
 * `loadInputs()` resolves to the export inputs, or null when there's nothing to export.
 */
export function startImageExport({ loadInputs, filenameOf, officeId, choice = "auto", expected = false }) {
  const result = { filename: "", custom: false, warning: null };
  const blobPromise = loadInputs().then(async (inputs) => {
    if (!inputs) throw new Error(NOTHING_TO_EXPORT);
    result.filename = filenameOf(inputs);
    const chosen = await choose(inputs, { officeId, choice, expected }, result);
    if (chosen) {
      try {
        const blob = await paintFormatImage(chosen.format.template, chosen.values, chosen.images);
        result.custom = true;
        return blob;
      } catch (err) {
        console.error("Drawing the office's breakdown format failed:", err);
        result.warning = FALLBACK_WARNING;
      }
    }
    return renderBreakdownImage(inputs);
  });
  const copied = copyBlobToClipboard("image/png", blobPromise);
  return { blobPromise, copied, result };
}

/** Copy Text. `kind` ("detailed" | "short") only matters for the standard text. */
export function startTextExport({ loadInputs, officeId, choice = "auto", expected = false, kind = "detailed" }) {
  const result = { custom: false, warning: null };
  const textPromise = loadInputs().then(async (inputs) => {
    if (!inputs) throw new Error(NOTHING_TO_EXPORT);
    const chosen = await choose(inputs, { officeId, choice, expected, withImages: false }, result);
    if (chosen) {
      try {
        const text = renderFormatText(chosen.format.template, chosen.values, chosen.model);
        result.custom = true;
        return text;
      } catch (err) {
        console.error("Writing the office's breakdown format failed:", err);
        result.warning = FALLBACK_WARNING;
      }
    }
    const model = buildBreakdownExportModel(inputs);
    return kind === "short" ? renderShortFormText(model) : renderPlanInfoText(model);
  });
  const copied = copyTextToClipboard(textPromise);
  return { textPromise, copied, result };
}

/** Print PDF. Resolves to {custom, warning}, or null when there's nothing to print. */
export async function runPrintExport({ loadInputs, officeId, choice = "auto", expected = false, title }) {
  const inputs = await loadInputs();
  if (!inputs) return null;
  const result = { custom: false, warning: null };
  const chosen = await choose(inputs, { officeId, choice, expected }, result);
  if (chosen) {
    try {
      await printFormatPages(paintPages(chosen.format.template, chosen.values, chosen.images), { title });
      result.custom = true;
      return result;
    } catch (err) {
      console.error("Printing the office's breakdown format failed:", err);
      result.warning = FALLBACK_WARNING;
    }
  }
  await printHtmlDocument({ html: buildBreakdownPrintHtml(inputs) });
  return result;
}
